import React, { useState, useEffect } from 'react';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Select } from '../components/Select';
import { Loading } from '../components/Loading';
import { saleService } from '../services/saleService';
import { itemService } from '../services/itemService';
import { exportService } from '../services/exportService';
import { Sale, Item } from '../types';
import { format } from 'date-fns';
import { formatCurrency } from '../utils/currency';
import { showToast } from '../utils/toast';

export const Sales: React.FC = () => {
  const [sales, setSales] = useState<Sale[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSale, setEditingSale] = useState<Sale | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    item_id: '',
    start_date: '',
    end_date: '',
    min_amount: '',
    max_amount: '',
    sort_by: 'date' as 'date' | 'amount' | 'quantity',
    sort_order: 'desc' as 'asc' | 'desc',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [formData, setFormData] = useState({
    item_id: '',
    quantity_sold: '',
    selling_price: '',
    date: format(new Date(), 'yyyy-MM-dd'),
  });

  // Clear selling price when item changes (to prevent old values)
  useEffect(() => {
    if (formData.item_id && !editingSale && showForm) {
      // Clear selling price when new item is selected (only if form is open and not editing)
      setFormData((prev) => ({
        ...prev,
        selling_price: '',
      }));
    }
  }, [formData.item_id, showForm]);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [exporting, setExporting] = useState<'pdf' | 'excel' | null>(null);

  // Calculate total profit from all visible sales
  const totalProfit = sales.reduce((sum, sale) => sum + (sale.profit || 0), 0);
  const totalSalesAmount = sales.reduce((sum, sale) => sum + (sale.total_amount || 0), 0);

  useEffect(() => {
    loadSales();
  }, [currentPage, filters]);

  // Debounced item search
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      loadItems();
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  useEffect(() => {
    if (formData.item_id) {
      const item = items.find((i) => i.id === parseInt(formData.item_id));
      if (item) {
        setSelectedItem(item);
        // Don't auto-populate selling price - let company decide
      }
    } else {
      setSelectedItem(null);
    }
  }, [formData.item_id, items]);

  const loadSales = async () => {
    try {
      setLoading(true);
      const filterParams: any = {
        sort_by: filters.sort_by,
        sort_order: filters.sort_order,
      };
      if (filters.item_id) {
        filterParams.item_id = parseInt(filters.item_id);
      }
      if (filters.start_date) {
        filterParams.start_date = filters.start_date;
      }
      if (filters.end_date) {
        filterParams.end_date = filters.end_date;
      }
      if (filters.min_amount) {
        filterParams.min_amount = parseFloat(filters.min_amount);
      }
      if (filters.max_amount) {
        filterParams.max_amount = parseFloat(filters.max_amount);
      }
      const response = await saleService.getSales(currentPage, 20, filterParams);
      if (response.success && response.data) {
        setSales(response.data.sales);
        setTotalPages(response.data.pagination.pages);
      } else {
        showToast.error(response.message || 'Failed to load sales');
      }
    } catch (err: any) {
      if (err.response?.status !== 401 && err.response?.status !== 422) {
        showToast.error(err.message || 'Failed to load sales');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadItems = async () => {
    try {
      let response;
      if (searchQuery) {
        // Use server-side search if query exists
        response = await itemService.searchItems(searchQuery);
      } else {
        // Otherwise load default list (increased limit to 1000 to see more items)
        // Sort by created_at desc to show newest inventory first
        const filterParams = {
          sort_by: 'created_at',
          sort_order: 'desc'
        };
        response = await itemService.getItems(1, 1000, undefined, filterParams as any);
      }

      if (response.success && response.data) {
        // searchItems returns array directly, getItems returns { items: ... }
        const itemsList = Array.isArray(response.data) ? response.data : response.data.items;
        setItems(itemsList);
      }
    } catch (err) {
      console.error('Failed to load items:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (!selectedItem) {
        showToast.error('Please select an item');
        setSubmitting(false);
        return;
      }

      const data = {
        item_id: parseInt(formData.item_id),
        quantity_sold: parseInt(formData.quantity_sold),
        selling_price: parseFloat(formData.selling_price),
        standard_price: selectedItem.standard_price,
        date: formData.date,
      };

      if (editingSale) {
        await saleService.updateSale(editingSale.id, {
          quantity_sold: data.quantity_sold,
          selling_price: data.selling_price,
          date: data.date,
        });
        showToast.success('Sale updated successfully');
      } else {
        await saleService.createSale(data);
        showToast.success('Sale recorded successfully');
      }

      resetForm();
      loadSales();
      loadItems(); // Reload items to update stock
    } catch (err: any) {
      showToast.error(err.response?.data?.message || err.message || 'Failed to save sale');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this sale? Stock will be restored.')) {
      return;
    }

    try {
      await saleService.deleteSale(id);
      showToast.success('Sale deleted successfully');
      loadSales();
      loadItems(); // Reload items to update stock
    } catch (err: any) {
      showToast.error(err.response?.data?.message || err.message || 'Failed to delete sale');
    }
  };

  const handleEdit = (sale: Sale) => {
    setEditingSale(sale);
    setFormData({
      item_id: sale.item_id.toString(),
      quantity_sold: sale.quantity_sold.toString(),
      selling_price: sale.selling_price.toString(),
      date: sale.date,
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      item_id: '',
      quantity_sold: '',
      selling_price: '',
      date: format(new Date(), 'yyyy-MM-dd'),
    });
    setEditingSale(null);
    setSelectedItem(null);
    setShowForm(false);
  };


  const handleExportPDF = async () => {
    try {
      setExporting('pdf');

      const params: any = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.item_id) params.item_id = parseInt(filters.item_id);

      const blob = await exportService.exportSalesPDF(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales_report_${format(new Date(), 'yyyyMMdd_HHmmss')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showToast.success('PDF exported successfully');
    } catch (err: any) {
      showToast.error(err.message || 'Failed to export PDF');
    } finally {
      setExporting(null);
    }
  };

  const handleExportExcel = async () => {
    try {
      setExporting('excel');

      const params: any = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.item_id) params.item_id = parseInt(filters.item_id);

      const blob = await exportService.exportSalesExcel(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales_report_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showToast.success('Excel exported successfully');
    } catch (err: any) {
      showToast.error(err.message || 'Failed to export Excel');
    } finally {
      setExporting(null);
    }
  };

  // Client-side filtering removed in favor of server-side search
  const filteredItems = items;

  if (loading && sales.length === 0) {
    return <Loading fullScreen text="Loading sales..." />;
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Sales Management</h1>
        <Button
          onClick={() => {
            if (!showForm) {
              resetForm(); // Reset form when opening
            }
            setShowForm(!showForm);
          }}
        >
          {showForm ? 'Cancel' : '+ Record Sale'}
        </Button>
      </div>


      {showForm && (
        <div className="mb-6 flex justify-center">
          <div className="w-full max-w-2xl">
            <Card title={editingSale ? 'Edit Sale' : 'Record New Sale'} className="shadow-lg">
              <form onSubmit={handleSubmit} className="space-y-6" onReset={resetForm}>
                <div className="space-y-6">
                  <div>
                    <Input
                      label="Search Item"
                      type="text"
                      placeholder="Search by name, brand, or size..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <div className="mt-4">
                      <Select
                        label="Select Item"
                        value={formData.item_id}
                        onChange={(e) => setFormData({ ...formData, item_id: e.target.value })}
                        required
                      >
                        <option value="">Select an item</option>
                        {filteredItems.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name} ({item.brand}{item.size ? ` - ${item.size}` : ''}) - Stock: {item.quantity}
                          </option>
                        ))}
                      </Select>
                    </div>
                    {selectedItem && (
                      <p className="mt-2 text-sm text-gray-600">
                        Available stock: <span className="font-semibold">{selectedItem.quantity}</span>
                      </p>
                    )}
                  </div>
                  <Input
                    label="Sale Date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                  <Input
                    label="Quantity"
                    type="number"
                    min="1"
                    max={selectedItem?.quantity || ''}
                    value={formData.quantity_sold}
                    onChange={(e) => setFormData({ ...formData, quantity_sold: e.target.value })}
                    required
                  />
                  {selectedItem && (
                    <div className="relative">
                      <label className="absolute left-0 top-0 text-xs text-gray-500 font-medium transform -translate-y-1">
                        Standard Price (Buying Cost) - Ksh
                      </label>
                      <input
                        type="number"
                        step="1"
                        value={Math.round(selectedItem.standard_price)}
                        readOnly
                        className="w-full pt-6 pb-2 px-0 bg-transparent border-0 border-b-2 border-gray-300 text-gray-600 cursor-not-allowed"
                      />
                    </div>
                  )}
                  <Input
                    label="Selling Price - Ksh"
                    type="number"
                    step="1"
                    min="0"
                    value={formData.selling_price}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only allow whole numbers
                      if (value === '' || /^\d+$/.test(value)) {
                        setFormData({ ...formData, selling_price: value });
                      }
                    }}
                    placeholder="Enter amount in Ksh (whole numbers only)"
                    required
                  />
                </div>
                {formData.quantity_sold && formData.selling_price && selectedItem && (
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600">
                        Profit per Item: <span className="text-lg font-bold text-green-600">
                          {formatCurrency(Math.round((parseFloat(formData.selling_price) || 0) - selectedItem.standard_price))}
                        </span>
                      </p>
                      <p className="text-sm text-gray-600">
                        Profit for This Sale: <span className="text-lg font-bold text-green-600">
                          {formatCurrency(Math.round(((parseFloat(formData.selling_price) || 0) - selectedItem.standard_price) * (parseInt(formData.quantity_sold) || 0)))}
                        </span>
                      </p>
                      <div className="pt-2 border-t border-green-300">
                        <p className="text-sm font-semibold text-gray-700">
                          Total Profit (All Sales): <span className="text-xl font-bold text-green-700">
                            {formatCurrency(totalProfit + Math.round(((parseFloat(formData.selling_price) || 0) - selectedItem.standard_price) * (parseInt(formData.quantity_sold) || 0)))}
                          </span>
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Current sales: {formatCurrency(totalProfit)} + This sale: {formatCurrency(Math.round(((parseFloat(formData.selling_price) || 0) - selectedItem.standard_price) * (parseInt(formData.quantity_sold) || 0)))}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex space-x-3 pt-4 border-t border-gray-200">
                  <Button type="submit" disabled={submitting} className="flex-1">
                    {submitting ? 'Saving...' : editingSale ? 'Update Sale' : 'Record Sale'}
                  </Button>
                  <Button type="button" variant="secondary" onClick={resetForm} className="flex-1">
                    Cancel
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        </div>
      )}

      <Card
        title="Sales History"
        headerAction={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => setShowFilters(!showFilters)}
              size="sm"
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
            <Button
              variant="secondary"
              onClick={handleExportPDF}
              disabled={exporting === 'pdf'}
              size="sm"
            >
              {exporting === 'pdf' ? 'Exporting...' : '📄 PDF'}
            </Button>
            <Button
              variant="secondary"
              onClick={handleExportExcel}
              disabled={exporting === 'excel'}
              size="sm"
            >
              {exporting === 'excel' ? 'Exporting...' : '📊 Excel'}
            </Button>
          </div>
        }
      >
        {/* Summary Cards */}
        {sales.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-gray-600">Total Sales</p>
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(totalSalesAmount)}
              </p>
              <p className="text-xs text-gray-500 mt-1">{sales.length} record{sales.length !== 1 ? 's' : ''}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm text-gray-600">Total Profit</p>
              <p className={`text-2xl font-bold ${totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(totalProfit)}
              </p>
              <p className="text-xs text-gray-500 mt-1">From visible sales</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <p className="text-sm text-gray-600">Average Profit</p>
              <p className={`text-2xl font-bold ${totalProfit >= 0 ? 'text-purple-600' : 'text-red-600'}`}>
                {formatCurrency(sales.length > 0 ? totalProfit / sales.length : 0)}
              </p>
              <p className="text-xs text-gray-500 mt-1">Per sale</p>
            </div>
          </div>
        )}
        {/* Advanced Filters */}
        {showFilters && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Advanced Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <Input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => {
                    setFilters({ ...filters, start_date: e.target.value });
                    setCurrentPage(1);
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <Input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => {
                    setFilters({ ...filters, end_date: e.target.value });
                    setCurrentPage(1);
                  }}
                />
              </div>
              <Select
                label="Item"
                value={filters.item_id}
                onChange={(e) => {
                  setFilters({ ...filters, item_id: e.target.value });
                  setCurrentPage(1);
                }}
              >
                <option value="">All Items</option>
                {items.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </Select>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Min Amount (Ksh)
                </label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={filters.min_amount}
                  onChange={(e) => {
                    setFilters({ ...filters, min_amount: e.target.value });
                    setCurrentPage(1);
                  }}
                  placeholder="Min"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Amount (Ksh)
                </label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={filters.max_amount}
                  onChange={(e) => {
                    setFilters({ ...filters, max_amount: e.target.value });
                    setCurrentPage(1);
                  }}
                  placeholder="Max"
                />
              </div>
              <Select
                label="Sort By"
                value={filters.sort_by}
                onChange={(e) => {
                  setFilters({ ...filters, sort_by: e.target.value as any });
                  setCurrentPage(1);
                }}
              >
                <option value="date">Date</option>
                <option value="amount">Amount</option>
                <option value="quantity">Quantity</option>
              </Select>
              <Select
                label="Order"
                value={filters.sort_order}
                onChange={(e) => {
                  setFilters({ ...filters, sort_order: e.target.value as any });
                  setCurrentPage(1);
                }}
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </Select>
            </div>
            <div className="mt-4">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setFilters({
                    item_id: '',
                    start_date: '',
                    end_date: '',
                    min_amount: '',
                    max_amount: '',
                    sort_by: 'date',
                    sort_order: 'desc',
                  });
                  setCurrentPage(1);
                }}
              >
                Clear Filters
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <Loading text="Loading sales..." />
        ) : sales.length === 0 ? (
          <p className="text-gray-600 text-center py-8">No sales found</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Item
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Brand
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Selling Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Profit
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sales.map((sale) => (
                    <tr key={sale.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {format(new Date(sale.date), 'MMM dd, yyyy')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {sale.item_name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {sale.item_brand || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {sale.quantity_sold}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatCurrency(sale.selling_price)}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm font-semibold ${sale.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(sale.profit)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => handleEdit(sale)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(sale.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between">
                <Button
                  variant="secondary"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="secondary"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};
